// Copyright (c) StrangeBee 2023

import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { useLocalMessage } from '@Helpers/locale';
import { useDrawer } from '@Hooks/useDrawer';
import { CreateListItem } from '@Molecules/CreateListItem/CreateListItem';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THForm } from '@Molecules/THForm/THForm';
import { FormInstance } from 'antd';
import { ReactElement } from 'react';
import RsaJS from 'jsrsasign';
import { CertificateImport } from '../CertificateImport/CertificateImport';
import './CertificateList.scss';
import { handleNotificationError } from '@Helpers/notification';
import { ICONS } from '@Constants/iconsApp';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { PEMCertificateInput } from 'thehive-sdk';

interface CertificateListProps {
    name: string[];
    form: FormInstance;
    hidden?: boolean;
}

export const CertificateList = ({ name, form, hidden }: CertificateListProps) => {
    const i = useLocalMessage();
    const certParser = new RsaJS.X509();

    const { visible, setVisible } = useDrawer<PEMCertificateInput>({});

    const getOrganisationName = (certificate: string) => {
        try {
            certParser.readCertPEM(certificate);
            const organisationName =
                certParser
                    ?.getSubject()
                    .array.flatMap((element) => element)
                    .find(({ type }) => type === 'O')?.value || 'Undefined Name';

            return organisationName;
        } catch (e) {
            handleNotificationError({
                type: i('sslConfig.field.certificateAuthorities.error.name'),
                message: i('sslConfig.field.certificateAuthorities.error.help'),
            });
            return;
        }
    };

    const addButton = (
        <THButton type="link" onClick={() => setVisible(true)}>
            {i('sslConfig.field.certificateAuthorities.add')}
        </THButton>
    );

    return (
        <>
            <THForm.Item
                label={i('sslConfig.field.certificateAuthorities.name')}
                name={name}
                extraAction={addButton}
                className="certificate-list"
                hidden={hidden}
            >
                <THForm.List name={name}>
                    {(fields, { remove, add }): ReactElement => (
                        <>
                            {!fields.length && (
                                <>
                                    <DisplayEmptyData
                                        className="certificate-list__empty"
                                        label={i('sslConfig.field.certificateAuthorities.empty')}
                                        image={<ICONS.CertificateAuthorityIcon />}
                                    >
                                        {addButton}
                                        <THTooltip title={i('sslConfig.field.certificateAuthorities.tooltip')}>
                                            <ICONS.QuestionCircleOutlined className="certificate-list__tooltip" />
                                        </THTooltip>
                                    </DisplayEmptyData>
                                </>
                            )}
                            {fields.map((field) => {
                                const certificate = form.getFieldValue(name)?.[field.name] as
                                    | PEMCertificateInput
                                    | undefined;

                                if (certificate) {
                                    const organisationName = getOrganisationName(certificate.data);
                                    if (!organisationName) {
                                        remove(fields.length - 1);
                                        return;
                                    }
                                    return (
                                        <CreateListItem
                                            title={organisationName}
                                            field={field}
                                            form={form}
                                            remove={remove}
                                            key={field.key}
                                        />
                                    );
                                }
                            })}
                            {visible && (
                                <CertificateImport
                                    name={name}
                                    form={form}
                                    visible={visible}
                                    onVisibleChange={setVisible}
                                    add={add}
                                />
                            )}
                        </>
                    )}
                </THForm.List>
            </THForm.Item>
        </>
    );
};
